import { db, storage } from '../../../context/Firebase';
import {
    collection,
    query,
    where,
    getDocs,
    doc,
    getDoc,
    addDoc,
    updateDoc,
    onSnapshot,
    orderBy,
    limit,
    serverTimestamp,
    startAfter,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import ChatRepository from '../../../domain/repositories/ChatRepository';
import ChatUser from '../../../domain/entities/ChatUser';
import Message from '../../../domain/entities/Message';
import Conversation from '../../../domain/entities/Conversation';

class FirebaseChatRepository extends ChatRepository {
    constructor() {
        super();
        this.chatsCollection = collection(db, 'chats');
        this.usersCollection = collection(db, 'users');
    }

    _toChatUserEntity(docSnap) {
        const data = docSnap.data();
        return new ChatUser({
            uid: docSnap.id,
            name: data.name || data.displayName || 'Unknown User',
            email: data.email,
            profilePic: data.picture || null,
        });
    }

    _toMessageEntity(docSnap, conversationId) {
        const data = docSnap.data();
        let messageText = '';
        if (typeof data.message === 'string') {
            messageText = data.message;
        } else if (data.message && typeof data.message === 'object') {
            messageText = data.message.text || '';
        }
        return new Message({
            id: docSnap.id || 'unknown',
            conversationId: conversationId || data.chatId || 'unknown',
            senderId: data.senderId || 'unknown',
            senderName: data.senderName || 'Unknown',
            message: messageText || ' ',
            timestamp: data.timestamp ? (data.timestamp.toDate ? data.timestamp.toDate() : new Date(data.timestamp)) : new Date(),
            type: data.type || 'text',
            imageUrl: data.imageUrl || data.images?.[0] || null,
            isDeleted: data.isDeleted || false,
            isSeen: data.isSeen || false,
        });
    }

    _toConversationEntity(docSnap) {
        const data = docSnap.data();
        return new Conversation({
            id: docSnap.id,
            participants: data.participants || [],
            participantNames: data.participantNames || [],
            lastMessage: data.lastMessage || null,
            lastMessageSenderId: data.lastMessageSendersId || null,
            lastMessageTimestamp: data.lastMessageTimestamp?.toDate ? data.lastMessageTimestamp.toDate() : null,
            createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(),
            type: data.type || 'one-to-one',
            name: data.name || null,
            adminIds: data.adminIds || [],
        });
    }

    async getChatUser(uid) {
        const docRef = doc(this.usersCollection, uid);
        const docSnap = await getDoc(docRef);
        return docSnap.exists() ? this._toChatUserEntity(docSnap) : null;
    }

    async getChatUserByEmail(email) {
        const q = query(this.usersCollection, where('email', '==', email));
        const snapshot = await getDocs(q);
        return !snapshot.empty ? this._toChatUserEntity(snapshot.docs[0]) : null;
    }

    async getConversationById(conversationId) {
        const docRef = doc(this.chatsCollection, conversationId);
        const docSnap = await getDoc(docRef);
        return docSnap.exists() ? this._toConversationEntity(docSnap) : null;
    }

    async getConversations(userId) {
        const q = query(this.chatsCollection, where('participants', 'array-contains', userId));
        const snapshot = await getDocs(q);
        return snapshot.docs.map(d => this._toConversationEntity(d));
    }

    async getOrCreateOneToOneConversation(currentUserId, currentUserName, otherUserId, otherUserName) {
        // Check for existing conversation first
        const q = query(
            this.chatsCollection,
            where('type', '==', 'one-to-one'),
            where('participants', 'array-contains', currentUserId)
        );
        const snapshot = await getDocs(q);
        const existing = snapshot.docs.find(d => (d.data().participants || []).includes(otherUserId));
        if (existing) return this._toConversationEntity(existing);

        // Create new conversation
        const newConv = {
            type: 'one-to-one',
            participants: [currentUserId, otherUserId],
            participantNames: [currentUserName, otherUserName],
            lastMessage: null,
            lastMessageSendersId: null,
            lastMessageTimestamp: null,
            createdAt: serverTimestamp(),
            name: null,
            adminIds: [],
        };
        const docRef = await addDoc(this.chatsCollection, newConv);
        
        // Re-check for race condition after creation
        const recheckSnapshot = await getDocs(q);
        const duplicate = recheckSnapshot.docs.find(d => 
            d.id !== docRef.id && (d.data().participants || []).includes(otherUserId)
        );
        
        if (duplicate) {
            // Another conversation was created concurrently, use that one
            return this._toConversationEntity(duplicate);
        }
        
        return new Conversation({ ...newConv, id: docRef.id, createdAt: new Date() });
    }

    async createGroupConversation(groupName, participantIds, participantNames, adminIds) {
        const newConv = {
            type: 'group',
            name: groupName,
            participants: participantIds,
            participantNames,
            adminIds,
            lastMessage: null,
            lastMessageSendersId: null,
            lastMessageTimestamp: null,
            createdAt: serverTimestamp(),
        };
        const docRef = await addDoc(this.chatsCollection, newConv);
        return new Conversation({ ...newConv, id: docRef.id, createdAt: new Date() });
    }

    async sendMessage(conversationId, senderId, senderName, messageText, type = 'text', imageFile = null) {
        let imageUrl = null;
        if (type === 'image' && imageFile) {
            const imageRef = ref(storage, `chat_images/${conversationId}/${Date.now()}-${imageFile.name}`);
            const snap = await uploadBytes(imageRef, imageFile);
            imageUrl = await getDownloadURL(snap.ref);
        }
        const messagesRef = collection(db, 'chats', conversationId, 'messages');
        const messageData = {
            senderId,
            senderName,
            message: messageText,
            timestamp: serverTimestamp(),
            type,
            imageUrl,
            isDeleted: false,
            isSeen: false,
        };
        
        // Create message and update conversation metadata atomically
        const msgDocRef = await addDoc(messagesRef, messageData);
        const conversationRef = doc(this.chatsCollection, conversationId);
        
        try {
            await updateDoc(conversationRef, {
                lastMessage: messageText,
                lastMessageSendersId: senderId,
                lastMessageTimestamp: serverTimestamp(),
            });
        } catch (error) {
            console.error('Failed to update conversation metadata:', error);
            // Message was created but metadata update failed - log for manual recovery
            throw new Error('Message sent but conversation summary update failed');
        }
        
        return new Message({ ...messageData, id: msgDocRef.id, conversationId, timestamp: new Date() });
    }

    async editMessage(conversationId, messageId, newText) {
        await updateDoc(doc(db, 'chats', conversationId, 'messages', messageId), { message: newText });
    }

    async deleteMessage(conversationId, messageId) {
        await updateDoc(doc(db, 'chats', conversationId, 'messages', messageId), {
            isDeleted: true,
            message: 'This message was deleted',
        });
    }

    onMessagesUpdate(conversationId, onMessagesUpdate) {
        const messagesRef = collection(db, 'chats', conversationId, 'messages');
        const q = query(messagesRef, orderBy('timestamp', 'asc'));
        return onSnapshot(q, (snapshot) => {
            const messages = snapshot.docs.map(d => this._toMessageEntity(d, conversationId));
            onMessagesUpdate(messages);
        }, (error) => {
            console.error('Error listening to messages:', error);
            onMessagesUpdate([]);
        });
    }

    onRecentMessagesUpdate(conversationId, messageLimit, onMessagesUpdate) {
        const messagesRef = collection(db, 'chats', conversationId, 'messages');
        const q = query(messagesRef, orderBy('timestamp', 'desc'), limit(messageLimit));
        return onSnapshot(q, (snapshot) => {
            const messages = snapshot.docs
                .map(d => ({ entity: this._toMessageEntity(d, conversationId), doc: d }))
                .reverse();
            const firstDoc = snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : null;
            onMessagesUpdate(messages.map(m => m.entity), firstDoc, snapshot.docs.length < messageLimit);
        }, (error) => {
            console.error('Error listening to messages:', error);
            onMessagesUpdate([], null, true);
        });
    }

    async getOlderMessages(conversationId, beforeDoc, messageLimit) {
        const messagesRef = collection(db, 'chats', conversationId, 'messages');
        const q = query(messagesRef, orderBy('timestamp', 'desc'), startAfter(beforeDoc), limit(messageLimit));
        const snapshot = await getDocs(q);
        const messages = snapshot.docs.map(d => this._toMessageEntity(d, conversationId)).reverse();
        const firstDoc = snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : null;
        return { messages, firstDoc, hasReachedEnd: snapshot.docs.length < messageLimit };
    }
}

export default FirebaseChatRepository;
